import React, { Component } from 'react';
import { Button, InputNumber, Select, Spin } from 'antd';
import { fetchData } from '../lib/apis';

const { Option } = Select;
const fallbackProductImage = require('../img/no_images.png');

export function getDefaultVariantImage(variant = {}) {
    const attachments = Array.isArray(variant.attachments) ? variant.attachments : [];
    const firstAttachment = attachments.find((item) => item && item.url);

    return `${variant.image || variant.product_image || variant.picture || (firstAttachment && firstAttachment.url) || ''}`.trim();
}

export default class OrderVariantPicker extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            variants: [],
            selectedVariantId: undefined,
            searchValue: '',
            selectRenderKey: 0
        };
    }

    componentDidMount() {
        this.searchVariants('');
    }

    formatCurrency = (value) => {
        return `${new Intl.NumberFormat('vi-VN').format(value || 0)}đ`;
    }

    getVariantAttributes = (variant) => {
        if (!variant) return [];

        const attributes = variant.attributes || variant.variant_attributes || {};
        return Object.keys(attributes)
            .filter((key) => `${attributes[key] || ''}`.trim())
            .map((key) => ({
                label: key,
                value: `${attributes[key]}`
            }));
    }

    renderVariantAttributes = (variant) => {
        const attributes = this.getVariantAttributes(variant);
        if (attributes.length === 0) return null;

        return (
            <div className="order-product-attributes">
                {attributes.map((attribute) => (
                    <span key={`${attribute.label}-${attribute.value}`} className="order-product-attribute-tag">
                        {attribute.label}: {attribute.value}
                    </span>
                ))}
            </div>
        );
    }

    renderVariantOption = (variant) => {
        return (
            <div className="order-product-option">
                <div className="order-product-option__head">
                    <span className="order-product-option__sku">{variant.sku}</span>
                    <span className="order-product-option__price">{this.formatCurrency(variant.sale_price || 0)}</span>
                </div>
                {this.renderVariantAttributes(variant)}
            </div>
        );
    }

    renderSelectedVariant = (item) => {
        const imageUrl = getDefaultVariantImage(item);

        return (
            <div className="order-product-table__product">
                <div className="order-product-table__media">
                    <button
                        type="button"
                        className={imageUrl ? 'order-product-table__image is-filled' : 'order-product-table__image'}
                        onClick={() => {
                            if (imageUrl) {
                                window.open(imageUrl, '_blank');
                            }
                        }}
                    >
                        {imageUrl ? (
                            <img
                                src={imageUrl}
                                alt={item.name || item.sku || 'variant'}
                                onError={(event) => {
                                    event.target.src = fallbackProductImage;
                                }}
                            />
                        ) : (
                            <i className="far fa-image" />
                        )}
                    </button>
                </div>
                <div className="order-product-table__content">
                    <div className="order-product-table__sku">{item.name}</div>
                    {this.renderVariantAttributes(item)}
                </div>
            </div>
        );
    }

    searchVariants = async (keyword = '') => {
        try {
            this.setState({ loading: true, searchValue: keyword });
            const response = await fetchData({
                url: `api/v1/product/variants?keyword=${encodeURIComponent(keyword)}&status=active&limit=20`
            });
            const activeVariants = Array.isArray(response.data)
                ? response.data.filter((item) => !item.status || item.status === 'active')
                : [];
            this.setState({ variants: activeVariants, loading: false });
        } catch (err) {
            this.setState({ loading: false });
        }
    };

    clearSelection = (callback) => {
        this.setState((prevState) => ({
            selectedVariantId: undefined,
            searchValue: '',
            selectRenderKey: prevState.selectRenderKey + 1
        }), callback);
    }

    handleSearchInput = (value) => {
        this.setState({ searchValue: value || '' });
        this.searchVariants(value || '');
    }

    handleRefresh = () => {
        this.clearSelection();
        this.searchVariants('');
    }

    handleDropdownVisibleChange = (open) => {
        if (!open && !this.state.selectedVariantId) {
            this.setState({ searchValue: '' });
        }
    };

    handleBlur = () => {
        if (!this.state.selectedVariantId) {
            this.setState({ searchValue: '' });
        }
    }

    handleSelectVariant = (value) => {
        const items = this.props.draftItems || [];
        const variant = this.state.variants.find((item) => `${item._id}` === `${value}`);
        if (!variant) return;

        const existingIndex = items.findIndex((item) => `${item.variant_id}` === `${variant._id}`);
        if (existingIndex > -1) {
            const nextItems = items.slice();
            nextItems[existingIndex] = {
                ...nextItems[existingIndex],
                image: nextItems[existingIndex].image || getDefaultVariantImage(variant),
                quantity: Number(nextItems[existingIndex].quantity || 0) + 1
            };
            this.clearSelection(() => {
                this.props.onChange(nextItems);
            });
            return;
        }

        const nextItems = items.concat([{
            variant_id: variant._id,
            sku: variant.sku,
            name: variant.name || variant.sku,
            sale_price: variant.sale_price,
            attributes: variant.attributes || variant.variant_attributes || {},
            image: getDefaultVariantImage(variant),
            quantity: 1
        }]);
        this.clearSelection(() => {
            this.props.onChange(nextItems);
        });
    }

    render() {
        const items = this.props.draftItems || [];
        return (
            <Spin spinning={this.state.loading}>
                <section className="order-form-card order-form-products-card">
                    <div className="order-form-card__header">
                        <div className="order-form-card__icon"><i className="fas fa-shopping-cart" /></div>
                        <div className="order-form-card__title">Thông tin đặt hàng</div>
                    </div>

                    <div className="order-product-search">
                        <label className="order-form-label">Chọn sản phẩm / Biến thể</label>
                        <div className="order-product-search__control">
                            <Select
                                key={this.state.selectRenderKey}
                                showSearch
                                value={this.state.selectedVariantId}
                                searchValue={this.state.searchValue}
                                style={{ width: '100%' }}
                                placeholder="Tìm sản phẩm hoặc biến thể..."
                                filterOption={false}
                                autoClearSearchValue
                                onSearch={this.handleSearchInput}
                                onSelect={this.handleSelectVariant}
                                onDropdownVisibleChange={this.handleDropdownVisibleChange}
                                onBlur={this.handleBlur}
                            >
                                {this.state.variants.map((item) => (
                                    <Option key={item._id} value={item._id}>
                                        {this.renderVariantOption(item)}
                                    </Option>
                                ))}
                            </Select>
                            <Button
                                className="order-product-search__refresh"
                                aria-label="Làm mới danh sách sản phẩm"
                                onClick={this.handleRefresh}
                            >
                                <i className="fas fa-sync-alt" />
                            </Button>
                        </div>
                    </div>

                    <div className="order-product-table-wrap">
                        <table className="list-product order-product-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '44%' }}>Biến thể</th>
                                    <th style={{ width: '22%' }}>SL</th>
                                    <th style={{ width: '16%' }}>Đơn giá</th>
                                    <th style={{ width: '18%' }}>Tổng</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="order-product-table__empty">Chưa có sản phẩm nào được thêm vào đơn.</td>
                                    </tr>
                                ) : items.map((item, index) => (
                                    <tr key={`${item.variant_id}-${index}`}>
                                        <td>
                                            {this.renderSelectedVariant({ ...item, __draft_index: index })}
                                        </td>
                                        <td>
                                            <div className="order-product-table__qty">
                                                <button
                                                    type="button"
                                                    className="order-qty-button"
                                                    onClick={() => {
                                                        const nextItems = items.slice();
                                                        const nextQuantity = Math.max(1, Number(item.quantity || 1) - 1);
                                                        nextItems[index] = { ...nextItems[index], quantity: nextQuantity };
                                                        this.props.onChange(nextItems);
                                                    }}
                                                >
                                                    -
                                                </button>
                                                <InputNumber
                                                    min={1}
                                                    controls={false}
                                                    value={item.quantity}
                                                    onChange={(value) => {
                                                        const nextItems = items.slice();
                                                        nextItems[index] = { ...nextItems[index], quantity: value || 1 };
                                                        this.props.onChange(nextItems);
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className="order-qty-button"
                                                    onClick={() => {
                                                        const nextItems = items.slice();
                                                        nextItems[index] = { ...nextItems[index], quantity: Number(item.quantity || 0) + 1 };
                                                        this.props.onChange(nextItems);
                                                    }}
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </td>
                                        <td>{this.formatCurrency(item.sale_price || 0)}</td>
                                        <td>
                                            <div className="order-product-table__total-cell">
                                                <span className="order-product-table__total">{this.formatCurrency((item.sale_price || 0) * (item.quantity || 0))}</span>
                                                <button
                                                    type="button"
                                                    className="order-product-table__remove"
                                                    onClick={() => {
                                                        const nextItems = items.filter((current, idx) => idx !== index);
                                                        this.props.onChange(nextItems);
                                                    }}
                                                >
                                                    <i className="fas fa-trash" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            </Spin>
        );
    }
}
